/* ─────────────────────────────────────────────────────────────────────────────
 *  registry.ts — single source of truth for categories, content, and search
 *  To add a new category: add a CategoryConfig entry + content items. Done.
 * ───────────────────────────────────────────────────────────────────────────── */

// ── Types ────────────────────────────────────────────────────────────────────

export interface ContentItem {
  category: string;
  link: string;
  name: string;
  date?: string;
  isNew?: boolean;
  bullets?: string[];
  excerpt?: string;
}

export interface SearchOption {
  name: string;
  value: string;
}

export interface CategoryConfig {
  slug: string;
  label: string;
  resultTime: string; // e.g. "0.69"
}

// ── Category configs ─────────────────────────────────────────────────────────

const CATEGORIES: CategoryConfig[] = [
  { slug: "experience", label: "Experience", resultTime: "0.69" },
  { slug: "works",      label: "Works",      resultTime: "0.43" },
  { slug: "writing",    label: "Writing",     resultTime: "0.43" },
  { slug: "social",     label: "Social",      resultTime: "0.57" },
];

// ── Content database ─────────────────────────────────────────────────────────

const ORIGIN = window.location.origin;

export const Content: ContentItem[] = [
  {
    category: "experience",
    link: `${ORIGIN}/about#amazon`,
    name: "Lead Software Development Engineer — Amazon.com LLC",
    date: "Jun 2021 – Present",
    isNew: true,
    bullets: [
      "Developed updates, modules, and components for Fire OS and Alexa on tablets.",
      "Built a GenAI image‑generation application and backend service (Rust + AWS Lambda).",
      "Conceptualized, designed and deployed several new customer‑facing applications.",
    ],
  },
  {
    category: "experience",
    link: `${ORIGIN}/about#adelphi`,
    name: "Research Assistant & Software Engineer — Adelphi University",
    date: "May 2019 – Apr 2021",
    bullets: [
      "Mentored computer‑science students in both group and one‑on‑one settings.",
      "Created Subitizing games with Alice Brown Early Learning Center to boost early‑childhood numeracy skills.",
    ],
  },
  {
    category: "experience",
    link: `${ORIGIN}/about#bcbs`,
    name: "Full‑Stack Software Engineer (Intern) — Blue Cross Blue Shield of Massachusetts",
    date: "Jun 2019 – Aug 2019",
    bullets: [
      "Built an SMS‑campaign application using Spring Boot and Twilio API.",
      "Developed Angular 7 + Express.js web‑service applications.",
      "Authored automation tests with Karma/Jasmine, Mocha/Chai, Jest, and Protractor.",
    ],
  },
  {
    category: "experience",
    link: `${ORIGIN}/about#anor`,
    name: "Full‑Stack Software Engineer — Anor Developers LLC",
    date: "Jul 2016 – Aug 2018",
    bullets: [
      "Developed five Android applications for private companies and NGOs.",
      "Delivered new modules and feature updates for mobile and web apps.",
      "Collaborated with clients to identify the most feasible, effective solutions.",
    ],
  },
  {
    category: "works",
    link: `${ORIGIN}`,
    name: "Jasur Shukurov (this website)",
    excerpt: `A personal website simulating the Google Search platform. Developed with HTML, CSS and React`,
  },
  {
    category: "works",
    link: "https://github.com/jasur-2902",
    name: "Cryptography Visualizer",
    excerpt: "Interactive visualization of encryption algorithms with step-by-step animations.",
  },
  {
    category: "works",
    link: "https://github.com/jasur-2902",
    name: "GeoGuess — Geography Guessing Game",
    excerpt: "Identify locations on a map based on clues and street-view imagery.",
  },
  {
    category: "works",
    link: "https://github.com/jasur-2902",
    name: "jpg2svg — Image to Vector Converter",
    excerpt: "Command-line tool and web interface for converting raster images to SVG.",
  },
  {
    category: "works",
    link: "https://github.com/jasur-2902",
    name: "TutCode — Interactive Coding Platform",
    excerpt: "Educational platform for beginner programmers with real-time code execution.",
  },
  {
    category: "writing",
    link: "https://github.com/jasur-2902",
    name: "Subitizing and Early Numeracy Research",
    date: "2020",
    excerpt: "Research on using interactive games to boost early-childhood numeracy skills at Alice Brown Early Learning Center.",
  },
  {
    category: "writing",
    link: "https://github.com/jasur-2902",
    name: "SVG Advantages and Disadvantages",
    excerpt: "A deep dive into when and why to use scalable vector graphics in modern web development.",
  },
  {
    category: "writing",
    link: "https://github.com/jasur-2902",
    name: "Building a Google Search Parody with React",
    excerpt: "How I turned a portfolio website into a full Google ecosystem clone.",
  },
  {
    category: "social",
    link: "https://github.com/jasur-2902",
    name: "GitHub — jasur-2902",
    excerpt: "Open source projects, contributions, and code repositories.",
  },
  {
    category: "social",
    link: "https://www.linkedin.com/in/jasurshukurov/",
    name: "LinkedIn — Jasur Shukurov",
    excerpt: "Professional profile, work experience, and connections.",
  },
  {
    category: "social",
    link: `${ORIGIN}/email`,
    name: "Email — jasur@shukurov.me",
    excerpt: "Send a message via the Gmail clone or reach out directly.",
  },
];

// ── Search options (dropdown suggestions) ────────────────────────────────────

export const SEARCH_OPTIONS: SearchOption[] = [
  { name: "everything about you", value: "all" },
  { name: "about", value: "about" },
  { name: "resume", value: "resume" },
  { name: "projects", value: "projects" },
  { name: "maps", value: "maps" },
  { name: "drive", value: "drive" },
  { name: "email", value: "email" },
  { name: "experience", value: "experience" },
  { name: "works", value: "works" },
];

// ── Public helpers ───────────────────────────────────────────────────────────

export function filterByCategory(slug: string): ContentItem[] {
  return Content.filter((item) => item.category === slug);
}

export function getCategoryConfig(slug: string): CategoryConfig | undefined {
  return CATEGORIES.find((c) => c.slug === slug);
}

export function getCategorySlugs(): string[] {
  return CATEGORIES.map((c) => c.slug);
}

export function getSearchOptions(): SearchOption[] {
  return SEARCH_OPTIONS;
}
